import { useTranslation } from 'react-i18next'
import { Head } from 'vite-react-ssg'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { motion } from 'framer-motion'

export function Datenschutz() {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  return (
    <>
      <Head>
        <html lang={i18n.language} />
        <title>
          {isGerman ? 'Datenschutzerklärung' : 'Privacy Policy'} | Jonas
          Fackelmeyer
        </title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-background-secondary border-b border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-foreground-secondary hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {isGerman ? 'Zurück zur Startseite' : 'Back to Homepage'}
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-8">
              {isGerman ? 'Datenschutzerklärung' : 'Privacy Policy'}
            </h1>

            <div className="prose prose-lg dark:prose-invert max-w-none space-y-8 text-foreground-secondary">
              {/* Einleitung */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman
                    ? '1. Datenschutz auf einen Blick'
                    : '1. Privacy at a Glance'}
                </h2>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {isGerman ? 'Allgemeine Hinweise' : 'General Information'}
                </h3>
                <p>
                  {isGerman
                    ? 'Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.'
                    : 'The following information provides a simple overview of what happens to your personal data when you visit this website. Personal data is any data that can be used to personally identify you.'}
                </p>
              </section>

              {/* Verantwortlicher */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman
                    ? '2. Verantwortlicher für die Datenverarbeitung'
                    : '2. Data Controller'}
                </h2>
                <p>{isGerman ? 'Verantwortlich für die Datenverarbeitung auf dieser Website ist:' : 'The data controller for this website is:'}</p>
                <address className="not-italic mt-4 p-4 bg-background-secondary rounded-lg">
                  <p className="font-medium text-foreground">Jonas Fackelmeyer</p>
                  <p>Am Waldschlößchen 22</p>
                  <p>58791 Werdohl</p>
                  <p>{isGerman ? 'Deutschland' : 'Germany'}</p>
                  <p className="mt-2">
                    E-Mail:{' '}
                    <a
                      href="mailto:jonas@fackelmeyer.com"
                      className="text-primary hover:underline"
                    >
                      jonas@fackelmeyer.com
                    </a>
                  </p>
                </address>
              </section>

              {/* Datenerfassung */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman
                    ? '3. Datenerfassung auf dieser Website'
                    : '3. Data Collection on This Website'}
                </h2>

                <h3 className="text-lg font-medium text-foreground mb-2 mt-6">
                  {isGerman ? 'Server-Log-Dateien' : 'Server Log Files'}
                </h3>
                <p>
                  {isGerman
                    ? 'Der Provider dieser Website erhebt und speichert automatisch Informationen in sogenannten Server-Log-Dateien, die Ihr Browser automatisch übermittelt. Dies sind:'
                    : 'The provider of this website automatically collects and stores information in so-called server log files, which your browser automatically transmits. These are:'}
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>{isGerman ? 'Browsertyp und Browserversion' : 'Browser type and version'}</li>
                  <li>{isGerman ? 'Verwendetes Betriebssystem' : 'Operating system used'}</li>
                  <li>{isGerman ? 'Referrer URL' : 'Referrer URL'}</li>
                  <li>{isGerman ? 'Hostname des zugreifenden Rechners' : 'Hostname of the accessing computer'}</li>
                  <li>{isGerman ? 'Uhrzeit der Serveranfrage' : 'Time of server request'}</li>
                  <li>{isGerman ? 'IP-Adresse' : 'IP address'}</li>
                </ul>
                <p className="mt-4">
                  {isGerman
                    ? 'Diese Daten werden nicht mit anderen Datenquellen zusammengeführt. Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner Website.'
                    : 'This data is not merged with other data sources. The collection of this data is based on Art. 6 para. 1 lit. f GDPR. The website operator has a legitimate interest in the technically error-free presentation and optimization of their website.'}
                </p>
              </section>

              {/* Kontaktformular */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? '4. Kontaktformular' : '4. Contact Form'}
                </h2>
                <p>
                  {isGerman
                    ? 'Wenn Sie mir per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei mir gespeichert.'
                    : 'When you send inquiries via the contact form, your information from the inquiry form, including the contact details you provide there, will be stored by me for the purpose of processing your inquiry and in case of follow-up questions.'}
                </p>
                <p className="mt-4">
                  {isGerman
                    ? 'Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf meinem berechtigten Interesse an der effektiven Bearbeitung der an mich gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO).'
                    : 'The processing of this data is based on Art. 6 para. 1 lit. b GDPR if your inquiry is related to the fulfillment of a contract or is necessary for the implementation of pre-contractual measures. In all other cases, the processing is based on my legitimate interest in the effective processing of inquiries addressed to me (Art. 6 para. 1 lit. f GDPR).'}
                </p>
                <p className="mt-4">
                  {isGerman
                    ? 'Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei mir, bis Sie mich zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt. Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben unberührt.'
                    : 'The data you enter in the contact form will remain with me until you ask me to delete it, revoke your consent to storage, or the purpose for data storage no longer applies. Mandatory statutory provisions – in particular retention periods – remain unaffected.'}
                </p>

                <h3 className="text-lg font-medium text-foreground mb-2 mt-6">
                  {isGerman ? 'Cloudflare Turnstile' : 'Cloudflare Turnstile'}
                </h3>
                <p>
                  {isGerman
                    ? 'Zum Schutz des Kontaktformulars vor Spam und Missbrauch verwende ich Cloudflare Turnstile. Dies ist ein Dienst der Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. Cloudflare Turnstile analysiert verschiedene Informationen (z.B. IP-Adresse, Verweildauer, Mausbewegungen), um zu erkennen, ob die Eingabe von einem Menschen oder einem automatisierten Programm erfolgt. Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Verhinderung von Spam).'
                    : 'To protect the contact form from spam and abuse, I use Cloudflare Turnstile. This is a service of Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. Cloudflare Turnstile analyzes various information (e.g., IP address, time spent, mouse movements) to determine whether the input is made by a human or an automated program. Processing is based on Art. 6 para. 1 lit. f GDPR (legitimate interest in preventing spam).'}
                </p>
                <p className="mt-2">
                  {isGerman ? 'Weitere Informationen:' : 'More information:'}{' '}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Cloudflare Privacy Policy
                  </a>
                </p>
              </section>

              {/* Cookies */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? '5. Cookies und Lokale Speicherung' : '5. Cookies and Local Storage'}
                </h2>
                <p>
                  {isGerman
                    ? 'Diese Website verwendet keine Tracking-Cookies. Es werden lediglich technisch notwendige Daten im lokalen Speicher (localStorage) Ihres Browsers gespeichert, um Ihre Präferenzen wie Sprachauswahl und Theme-Einstellung (Hell/Dunkel) zu speichern. Diese Daten verlassen niemals Ihren Browser und werden nicht an Server übertragen.'
                    : 'This website does not use tracking cookies. Only technically necessary data is stored in your browser\'s local storage to save your preferences such as language selection and theme setting (light/dark). This data never leaves your browser and is not transmitted to servers.'}
                </p>
              </section>

              {/* Hosting */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? '6. Hosting und Content Delivery Network (CDN)' : '6. Hosting and Content Delivery Network (CDN)'}
                </h2>

                <h3 className="text-lg font-medium text-foreground mb-2">
                  {isGerman ? 'Hosting' : 'Hosting'}
                </h3>
                <p>
                  {isGerman
                    ? 'Diese Website wird auf einem Server in Deutschland gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf diesem Server gespeichert. Hierbei kann es sich um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln.'
                    : 'This website is hosted on a server in Germany. The personal data collected on this website is stored on this server. This may include IP addresses, contact requests, meta and communication data, contact details, names, website access, and other data generated through a website.'}
                </p>

                <h3 className="text-lg font-medium text-foreground mb-2 mt-6">
                  Cloudflare CDN
                </h3>
                <p>
                  {isGerman
                    ? 'Ich nutze das Content Delivery Network (CDN) von Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. Cloudflare fungiert als Reverse-Proxy zwischen Ihrem Browser und meinem Server. Das bedeutet, dass alle Anfragen an diese Website über Cloudflare geleitet werden.'
                    : 'I use the Content Delivery Network (CDN) of Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. Cloudflare acts as a reverse proxy between your browser and my server. This means that all requests to this website are routed through Cloudflare.'}
                </p>
                <p className="mt-4">
                  {isGerman
                    ? 'Cloudflare verarbeitet dabei folgende Daten:'
                    : 'Cloudflare processes the following data:'}
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>{isGerman ? 'IP-Adresse' : 'IP address'}</li>
                  <li>{isGerman ? 'Systemkonfigurationsinformationen' : 'System configuration information'}</li>
                  <li>{isGerman ? 'Datum und Uhrzeit des Zugriffs' : 'Date and time of access'}</li>
                  <li>{isGerman ? 'Referrer-URL' : 'Referrer URL'}</li>
                </ul>
                <p className="mt-4">
                  {isGerman
                    ? 'Der Einsatz von Cloudflare erfolgt zum Schutz vor Cyberangriffen (DDoS-Schutz) und zur schnelleren Bereitstellung der Website. Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar. Cloudflare ist unter dem EU-US Data Privacy Framework zertifiziert.'
                    : 'The use of Cloudflare is for protection against cyber attacks (DDoS protection) and for faster delivery of the website. This represents a legitimate interest within the meaning of Art. 6 para. 1 lit. f GDPR. Cloudflare is certified under the EU-US Data Privacy Framework.'}
                </p>
                <p className="mt-2">
                  {isGerman ? 'Weitere Informationen:' : 'More information:'}{' '}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Cloudflare Privacy Policy
                  </a>
                </p>
              </section>

              {/* SSL/TLS */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? '7. SSL- bzw. TLS-Verschlüsselung' : '7. SSL/TLS Encryption'}
                </h2>
                <p>
                  {isGerman
                    ? 'Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Anfragen, die Sie an mich als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von "http://" auf "https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.'
                    : 'This site uses SSL or TLS encryption for security reasons and to protect the transmission of confidential content, such as inquiries you send to me as the site operator. You can recognize an encrypted connection by the fact that the address line of the browser changes from "http://" to "https://" and by the lock symbol in your browser line.'}
                </p>
              </section>

              {/* Betroffenenrechte */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman ? '8. Ihre Rechte' : '8. Your Rights'}
                </h2>
                <p>
                  {isGerman
                    ? 'Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf:'
                    : 'You have the right at any time within the framework of applicable legal provisions to:'}
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-2">
                  <li>
                    <strong>{isGerman ? 'Auskunft' : 'Information'}:</strong>{' '}
                    {isGerman
                      ? 'Unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten (Art. 15 DSGVO)'
                      : 'Free information about your stored personal data (Art. 15 GDPR)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Berichtigung' : 'Correction'}:</strong>{' '}
                    {isGerman
                      ? 'Berichtigung unrichtiger oder Vervollständigung unvollständiger Daten (Art. 16 DSGVO)'
                      : 'Correction of inaccurate or completion of incomplete data (Art. 16 GDPR)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Löschung' : 'Deletion'}:</strong>{' '}
                    {isGerman
                      ? 'Löschung Ihrer bei mir gespeicherten personenbezogenen Daten (Art. 17 DSGVO)'
                      : 'Deletion of your personal data stored with me (Art. 17 GDPR)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Einschränkung' : 'Restriction'}:</strong>{' '}
                    {isGerman
                      ? 'Einschränkung der Verarbeitung Ihrer personenbezogenen Daten (Art. 18 DSGVO)'
                      : 'Restriction of processing of your personal data (Art. 18 GDPR)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Datenübertragbarkeit' : 'Data Portability'}:</strong>{' '}
                    {isGerman
                      ? 'Erhalt Ihrer Daten in einem strukturierten, gängigen und maschinenlesbaren Format (Art. 20 DSGVO)'
                      : 'Receive your data in a structured, commonly used, and machine-readable format (Art. 20 GDPR)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Widerspruch' : 'Objection'}:</strong>{' '}
                    {isGerman
                      ? 'Widerspruch gegen die Verarbeitung Ihrer personenbezogenen Daten (Art. 21 DSGVO)'
                      : 'Object to the processing of your personal data (Art. 21 GDPR)'}
                  </li>
                  <li>
                    <strong>{isGerman ? 'Beschwerde' : 'Complaint'}:</strong>{' '}
                    {isGerman
                      ? 'Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO)'
                      : 'Lodge a complaint with a supervisory authority (Art. 77 GDPR)'}
                  </li>
                </ul>
                <p className="mt-4">
                  {isGerman
                    ? 'Wenn Sie von Ihren Rechten Gebrauch machen möchten, können Sie mich jederzeit per E-Mail kontaktieren.'
                    : 'If you wish to exercise your rights, you can contact me at any time by email.'}
                </p>
              </section>

              {/* Widerruf */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman
                    ? '9. Widerruf Ihrer Einwilligung zur Datenverarbeitung'
                    : '9. Revocation of Your Consent to Data Processing'}
                </h2>
                <p>
                  {isGerman
                    ? 'Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.'
                    : 'Many data processing operations are only possible with your express consent. You can revoke consent you have already given at any time. The legality of data processing carried out until the revocation remains unaffected by the revocation.'}
                </p>
              </section>

              {/* Änderungen */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  {isGerman
                    ? '10. Änderungen dieser Datenschutzerklärung'
                    : '10. Changes to This Privacy Policy'}
                </h2>
                <p>
                  {isGerman
                    ? 'Ich behalte mir vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen meiner Leistungen in der Datenschutzerklärung umzusetzen. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.'
                    : 'I reserve the right to adapt this privacy policy so that it always complies with current legal requirements or to implement changes to my services in the privacy policy. The new privacy policy will then apply to your next visit.'}
                </p>
                <p className="mt-4 text-foreground-muted">
                  {isGerman ? 'Stand: Februar 2025' : 'Last updated: February 2025'}
                </p>
              </section>
            </div>

            {/* Back Link */}
            <div className="mt-12 pt-8 border-t border-border">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                {isGerman ? 'Zurück zur Startseite' : 'Back to Homepage'}
              </Link>
            </div>
          </motion.article>
        </main>
      </div>
    </>
  )
}
